import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";

import { useEmployeeContext } from "../../context/EmployeeCtx";
import StepFooter from "../StepFooter";

import FormInput from "@/components/form-fields/form-input";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { Form } from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { EmployeeSuccessMessage } from "@/constants/employees/employee.constants";
import useQueryParams from "@/hooks/useQueryParams";
import {
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
} from "@/redux/api/employeeQuery";
import {
  AddEmployee,
  TAddEmployeeForm,
  TUpdateEmployee,
} from "@/schemas/employee-schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type BasicFormProps = {
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  operationType: "edit" | "add";
  setIsFormDirty: Dispatch<SetStateAction<boolean>>;
};
const BasicForm = ({
  setActiveTab,
  operationType,
  setIsFormDirty,
}: BasicFormProps): React.JSX.Element => {
  const { employeeId, basicInfo } = useEmployeeContext();
  const { updateParams } = useQueryParams();

  const form = useForm<TAddEmployeeForm | TUpdateEmployee>({
    resolver: zodResolver(AddEmployee),
    mode: "all",
    defaultValues: {
      address: "",
      describesYou: "",
      deviceId: "",
    },
    ...(employeeId && {
      values: basicInfo,
    }),
  });

  const {
    formState: { dirtyFields },
  } = form;

  useEffect(() => {
    if (dirtyFields && Object.keys(dirtyFields).length > 0) {
      setIsFormDirty(true);
    } else {
      setIsFormDirty(false);
    }
  }, [JSON.stringify(dirtyFields)]);

  const [addUserByWs, { isLoading: isAdding, isSuccess: isAdded }] =
    useAddEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating, isSuccess: isUpdated }] =
    useUpdateEmployeeMutation();

  // Mutation getter function to return the appropriate mutation with its state
  const getMutationStates = (): {
    isLoading: boolean;
    isSuccess: boolean;
  } => {
    if (employeeId) {
      return {
        isLoading: isUpdating,
        isSuccess: isUpdated,
      };
    } else {
      return {
        isLoading: isAdding,
        isSuccess: isAdded,
      };
    }
  };

  const { isLoading, isSuccess } = getMutationStates();

  const handleFormSubmit = form.handleSubmit(
    async (values: TUpdateEmployee) => {
      if (employeeId) {
        const res = await updateEmployee({
          employeeId,
          data: { ...values, permissions: [] },
        });
        validateResponse({
          response: res as MutationResponse,
          onSuccess: () => {
            customToast.success(EmployeeSuccessMessage.BASIC_UPDATE);
            setActiveTab("2");
          },
        });
      } else {
        const res = await addUserByWs({
          data: { ...values, permissions: [] },
        });
        validateResponse({
          response: res as MutationResponse,
          onSuccess: () => {
            customToast.success(EmployeeSuccessMessage.CREATED);
            const responseData = res as { data: { data: { id: string } } };
            updateParams("id", responseData?.data?.data?.id ?? "");
            setActiveTab("2");
          },
        });
      }
    },
  );

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (Object.keys(form.formState.dirtyFields).length) {
      await handleFormSubmit();
    } else {
      setActiveTab("2");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <h2 className='font-medium'>1. Basic Information</h2>
        <section className='grid grid-cols-1 gap-4 mt-4 text-xs lg:grid-cols-3'>
          <FormInput
            name='firstName'
            form={form}
            label='First Name'
            required
            input={{
              type: "text",
              placeholder: "Please enter your first name",
            }}
          />
          <FormInput
            name='middleName'
            form={form}
            label='Middle Name'
            input={{
              type: "text",
              placeholder: "Please enter your middle name",
            }}
          />
          <FormInput
            name='lastName'
            form={form}
            label='Last Name'
            required
            input={{
              type: "text",
              placeholder: "Please enter your last name",
            }}
          />
          <FormInput
            form={form}
            name='phoneNumber'
            label='Phone Number'
            required
            render={(field) => (
              <PhoneInput
                {...field}
                onChange={(value) => field.onChange(value)}
                value={field.value as string & { __tag: "E164Number" }}
                international
                defaultCountry='AU'
                allowedCountries={["AU", "NP"]}
                placeholder='(000) 000-0000'
              />
            )}
          />
          <FormInput
            name='email'
            form={form}
            label='Email'
            required
            input={{
              type: "text",
              placeholder: "Please enter your email",
            }}
          />
          <FormInput
            form={form}
            name='address'
            label='Address'
            required
            input={{
              type: "text",
              placeholder: "Please enter your address",
            }}
          />
          <FormInput
            form={form}
            name='whiteCardNo'
            label='White Card Number'
            required
            input={{
              placeholder: "Please enter while card number",
            }}
          />
          <FormInput
            form={form}
            name='describesYou'
            label='What Best Describes You'
            required
            render={(field) => (
              <FormSelectSingle
                field={field}
                options={[
                  { label: "Student", value: "Student" },
                  { label: "Employee", value: "Employee" },
                  { label: "Self-Employed", value: "Self-Employed" },
                  { label: "UnEmployed", value: "UnEmployed" },
                ]}
                placeholder='Please Select'
              />
            )}
          />
        </section>
        <StepFooter
          step={1}
          {...{ isLoading, isSuccess }}
          isActive={
            !employeeId && !Object.keys(form.formState.dirtyFields).length
          }
          disableBack
          reset={form.reset}
          operationType={operationType}
          setActiveTab={setActiveTab}
        />
      </form>
    </Form>
  );
};

export default BasicForm;
